import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  SaveAlt as ExportIcon,
  Upload as ImportIcon,
  Language as LanguageIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { PageTitleContainer } from '../components/common';
import useTranslationManagement from '../hooks/useTranslation';
import useLanguage from '../hooks/useLanguage';
import useAuth from '../hooks/useAuth';

const Translations = () => {
  const { user } = useAuth();
  const { availableLanguages, currentLanguage } = useLanguage();
  const {
    translations,
    loading,
    error,
    loadTranslations,
    createTranslation,
    updateTranslation,
    deleteTranslation,
    generateJsonFiles,
    importTranslationsFromJson
  } = useTranslationManagement();

  // Local state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [currentTranslation, setCurrentTranslation] = useState(null);
  const [formData, setFormData] = useState({
    namespace: 'translation',
    group: '',
    key: '',
    translations: {
      en: '',
      es: '',
      fr: '',
      nl: ''
    }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  // Refs for height calculation
  const titleHeaderRef = useRef(null);
  const mainContentRef = useRef(null);

  // Calculate content height dynamically
  useLayoutEffect(() => {
    const calculateHeight = () => {
      const titleHeaderHeight = titleHeaderRef.current?.offsetHeight || 80;
      const extraPadding = 130;
      const totalHeaderHeight = titleHeaderHeight + extraPadding;

      const newHeight = `calc(100vh - ${totalHeaderHeight}px)`;
      setContentHeight(newHeight);
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // Get unique groups from translations for filtering
  const groups = Array.isArray(translations) && translations.length > 0
    ? [...new Set(translations.map(t => t?.group).filter(Boolean))]
    : [];

  // Ensure translations is always an array
  const translationsArray = Array.isArray(translations) ? translations : [];

  // Filter translations based on search query and group filter
  const filteredTranslations = translationsArray.filter(translation => {
    const matchesSearch = searchQuery
      ? (translation.key?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        translation.translations?.en?.toLowerCase().includes(searchQuery.toLowerCase()))
      : true;

    const matchesGroup = filterGroup
      ? translation.group === filterGroup
      : true;

    return matchesSearch && matchesGroup;
  });

  // Pagination slice
  const paginatedTranslations = filteredTranslations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('translations.')) {
      // For translation fields (translations.en, translations.es, etc.)
      const lang = name.split('.')[1];
      setFormData({
        ...formData,
        translations: {
          ...formData.translations,
          [lang]: value
        }
      });
    } else {
      // For other fields
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Open create dialog
  const handleOpenCreateDialog = () => {
    setCurrentTranslation(null);
    setFormData({
      namespace: 'translation',
      group: '',
      key: '',
      translations: {
        en: '',
        es: '',
        fr: '',
        nl: ''
      }
    });
    setDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEditDialog = (translation) => {
    setCurrentTranslation(translation);
    setFormData({
      namespace: translation.namespace,
      group: translation.group,
      key: translation.key,
      translations: { ...translation.translations }
    });
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      if (currentTranslation) {
        // Update existing translation
        await updateTranslation(currentTranslation._id, formData);
      } else {
        // Create new translation
        await createTranslation(formData);
      }

      // Close dialog
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting translation:', error);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (translation) => {
    setCurrentTranslation(translation);
    setConfirmDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setConfirmDeleteDialogOpen(false);
  };

  // Confirm delete
  const handleDelete = async () => {
    try {
      if (currentTranslation) {
        await deleteTranslation(currentTranslation._id);
      }
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting translation:', error);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle search query change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // Reset to first page when search changes
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setFilterGroup('');
    setPage(0);
  };

  // Handle filter group change
  const handleFilterGroupChange = (e) => {
    setFilterGroup(e.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle reload
  const handleReload = () => {
    loadTranslations();
  };

  // Handle generate JSON
  const handleGenerateJson = async () => {
    try {
      await generateJsonFiles();
    } catch (error) {
      console.error('Error generating JSON:', error);
    }
  };

  // Handle import JSON
  const handleImportJson = async () => {
    try {
      await importTranslationsFromJson();
    } catch (error) {
      console.error('Error importing JSON:', error);
    }
  };

  // Check if user is admin
  const isAdmin = user?.systemRole === 'admin';

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">
            You do not have permission to access this page. Only administrators can manage translations.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      bgcolor: '#f5f6f7',
      minHeight: '100vh',
      pb: 4,
      px: 6,
      pt: 4
    }}>
      <PageTitleContainer
        ref={titleHeaderRef}
        title="Translation Management"
        rightContent={
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleReload}
              disabled={loading}
            >
              Reload
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ExportIcon />}
              onClick={handleGenerateJson}
              disabled={loading}
            >
              Generate JSON
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ImportIcon />}
              onClick={handleImportJson}
              disabled={loading}
            >
              Import from JSON
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              disabled={loading}
              sx={{
                textTransform: 'none !important'
              }}
            >
              Add translation
            </Button>
          </div>
        }
      />

      <Box sx={{  mx: 'auto', pt: 0, width: '100%' }}>
        <Box
          ref={mainContentRef}
          sx={{
            height: contentHeight,
            overflowY: 'auto',
            overflowX: 'hidden',
            pb: 2, background: 'white',
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              marginTop: '5px',
              marginBottom: '5px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.4)',
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: '4px',
              p: { xs: 2, sm: 3, md: 4, lg: '20px' },
              width: '100%',
              bgcolor: 'white',
              mb: 3,
            }}
          >
            {/* Statistics cards */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Total translations
                    </Typography>
                    <Typography variant="h4">
                      {loading ? <CircularProgress size={24} /> : (Array.isArray(translations) ? translations.length : 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Translation groups
                    </Typography>
                    <Typography variant="h4">
                      {loading ? <CircularProgress size={24} /> : groups.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Supported languages
                    </Typography>
                    <Typography variant="h4">
                      {loading ? <CircularProgress size={24} /> : availableLanguages.length}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, flexGrow: 1, alignContent: 'flex-start' }}>
                      {availableLanguages.map(lang => (
                        <Chip
                          key={lang.code}
                          label={lang.name}
                          size="small"
                          color={lang.code === currentLanguage ? "primary" : "default"}
                          variant={lang.code === currentLanguage ? "filled" : "outlined"}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Missing translations
                    </Typography>
                    <Typography variant="h4">
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        Array.isArray(translations)
                          ? translations.filter(t =>
                            !t?.translations?.es ||
                            !t?.translations?.fr ||
                            !t?.translations?.nl).length
                          : 0
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* Search and filters */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '4px',
              p: { xs: 2, sm: 3, md: 4, lg: '20px' },
              width: '100%',
              bgcolor: 'white',
              mb: 3
            }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Search translations"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search translations"
                  InputProps={{
                    endAdornment: searchQuery ? (
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    ) : (
                      <SearchIcon color="action" />
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      height: '45px',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="group-filter-label" sx={{
                    color: '#475969',
                    fontSize: '14px',
                    '&.Mui-focused': {
                      color: '#475969',
                    },
                  }}>Filter by group</InputLabel>
                  <Select
                    labelId="group-filter-label"
                    value={filterGroup}
                    onChange={handleFilterGroupChange}
                    label="Filter by Group"
                    sx={{
                      borderRadius: '4px',
                      height: '45px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4759694d',
                        border: '1px solid #4759694d',
                        borderRadius: '4px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '& .MuiSelect-select': {
                        fontSize: '14px',
                        color: 'var(--color-blue, #475969)',
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>All groups</em>
                    </MenuItem>
                    {groups.map(group => (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClearSearch}
                >
                  Clear filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Display error message if any */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Translations table */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '4px',
              overflow: 'hidden',
              width: '100%',
              bgcolor: 'white'
            }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label="All translations" />
                  <Tab label="Missing translations" />
                  {availableLanguages.map(lang => (
                    <Tab key={lang.code} label={`${lang.name} translations`} />
                  ))}
                </Tabs>

                <Box sx={{
                  mt: 2
                }}>
                  <TableContainer sx={{
                    height: " calc(100vh - 630px)",
                    // Custom scrollbar styling
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-track': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      marginTop: '5px',
                      marginBottom: '5px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.4)',
                      },
                    },
                    '&::-webkit-scrollbar-corner': {
                      backgroundColor: 'transparent',
                    },
                  }}>
                    <Table stickyHeader aria-label="translations table">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important', width: '10%' }}>Group</TableCell>
                          <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important', width: '10%' }}>Key</TableCell>
                          <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important', width: '20%' }}>English</TableCell>
                          {tabValue === 0 && (
                            <>
                              <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important', width: '15%' }}>Spanish</TableCell>
                              <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important', width: '15%' }}>French</TableCell>
                              <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important', width: '15%' }}>Dutch</TableCell>
                            </>
                          )}
                          {tabValue === 1 && (
                            <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important' }}>Missing languages</TableCell>
                          )}
                          {tabValue > 1 && (
                            <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important' }}>{availableLanguages[tabValue - 2].name}</TableCell>
                          )}
                          <TableCell sx={{ fontStyle: 'normal !important', fontSize: '16px !important', width: '15%' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedTranslations
                          .filter(translation => {
                            // For tab 1 (Missing Translations), filter out complete translations
                            if (tabValue === 1) {
                              return (
                                !translation.translations.es ||
                                !translation.translations.fr ||
                                !translation.translations.nl
                              );
                            }

                            // For language-specific tabs (2+), show all
                            return true;
                          })
                          .map((translation) => (
                            <TableRow key={translation._id}>
                              <TableCell>{translation.group}</TableCell>
                              <TableCell>{translation.key}</TableCell>
                              <TableCell>{translation.translations.en}</TableCell>

                              {tabValue === 0 && (
                                <>
                                  <TableCell>
                                    {translation.translations.es || (
                                      <Typography variant="body2" color="error">Missing</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {translation.translations.fr || (
                                      <Typography variant="body2" color="error">Missing</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {translation.translations.nl || (
                                      <Typography variant="body2" color="error">Missing</Typography>
                                    )}
                                  </TableCell>
                                </>
                              )}

                              {tabValue === 1 && (
                                <TableCell>
                                  {!translation.translations.es && <Chip size="small" label="ES" sx={{ mr: 0.5 }} />}
                                  {!translation.translations.fr && <Chip size="small" label="FR" sx={{ mr: 0.5 }} />}
                                  {!translation.translations.nl && <Chip size="small" label="NL" />}
                                </TableCell>
                              )}

                              {tabValue > 1 && (
                                <TableCell>
                                  {translation.translations[availableLanguages[tabValue - 2].code] || (
                                    <Typography variant="body2" color="error">Missing</Typography>
                                  )}
                                </TableCell>
                              )}

                              <TableCell>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleOpenEditDialog(translation)}
                                  size="small"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  onClick={() => handleOpenDeleteDialog(translation)}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}

                        {paginatedTranslations.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={tabValue === 0 ? 7 : (tabValue === 1 ? 5 : 5)} align="center">
                              <Typography variant="body1" sx={{ py: .5 }}>
                                No translations found. {searchQuery || filterGroup ? 'Try clearing the filters.' : ''}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {translationsArray.length > rowsPerPage && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                  minHeight: 52,
                  px: 3,
                }}>
                  {/* Left: displayed rows */}
                  <Typography sx={{ fontSize: '14px', color: '#475569', fontWeight: 500 }}>
                    {filteredTranslations.length === 0
                      ? '0–0 of 0'
                      : `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredTranslations.length)} of ${filteredTranslations.length}`}
                  </Typography>

                  {/* Center: pagination arrows */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleChangePage(e, page - 1)}
                      disabled={page === 0}
                      sx={{
                        color: '#475569',
                        '&:hover': { backgroundColor: 'rgba(71, 85, 105, 0.08)' },
                        '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.2)' },
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleChangePage(e, page + 1)}
                      disabled={page >= Math.ceil(filteredTranslations.length / rowsPerPage) - 1}
                      sx={{
                        color: '#475569',
                        '&:hover': { backgroundColor: 'rgba(71, 85, 105, 0.08)' },
                        '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.2)' },
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>

                  {/* Right: rows per page */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '14px', color: '#475569', fontWeight: 500 }}>
                      Rows per page:
                    </Typography>
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => {
                        handleChangeRowsPerPage({ target: { value: e.target.value } });
                      }}
                      size="small"
                      variant="standard"
                      disableUnderline
                      sx={{
                        fontSize: '14px',
                        color: '#475569',
                        fontWeight: 500,
                        '& .MuiSelect-select': {
                          paddingRight: '24px !important',
                          py: 0,
                        },
                      }}
                    >
                      {[10, 25, 50, 100].map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
                )}
              </>
            )}
          </Paper>
        </Box>

        {/* Translation dialog for create/edit */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              overflow: 'hidden',
              maxWidth: '60%'
            }
          }}
        >
          <DialogTitle>
            {currentTranslation ? 'Edit translation' : 'Add new translation'}
          </DialogTitle>
          <DialogContent dividers sx={{
            padding: '20px !important',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                  Group <span style={{ color: '#F89939' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="group"
                  value={formData.group}
                  onChange={handleChange}
                  required
                  placeholder="e.g. auth, common, errors"
                  helperText="Translation category, e.g. 'auth', 'common', 'errors'"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      height: '45px',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f44336',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                  Key <span style={{ color: '#F89939' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  required
                  placeholder="e.g. loginButton"
                  helperText="Unique identifier for this translation, e.g. 'loginButton'"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      height: '45px',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f44336',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                  English Translation <span style={{ color: '#F89939' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="translations.en"
                  value={formData.translations.en}
                  onChange={handleChange}
                  required
                  multiline
                  rows={2}
                  placeholder="Enter English translation"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f44336',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                  Spanish Translation
                </Typography>
                <TextField
                  fullWidth
                  name="translations.es"
                  value={formData.translations.es}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Enter Spanish translation"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                  French Translation
                </Typography>
                <TextField
                  fullWidth
                  name="translations.fr"
                  value={formData.translations.fr}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Enter French translation"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                  Dutch Translation
                </Typography>
                <TextField
                  fullWidth
                  name="translations.nl"
                  value={formData.translations.nl}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Enter Dutch translation"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{
            px: 3,
            py: 2
          }}>
            <Button
              onClick={handleCloseDialog}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!formData.key || !formData.group || !formData.translations.en}
              sx={{ textTransform: 'none' }}
            >
              {currentTranslation ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog open={confirmDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Delete Translation</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the translation for "{currentTranslation?.key}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Translations;